import { z } from 'zod';
import '@apexdesigner/schema-tools';
import { belongsTo, hasOne, references } from '@apexdesigner/schema-tools';

export const registryToken = z
  .object({
    id: z.number().hidden().describe('Unique identifier'),

    name: z
      .string()
      .nullable()
      .optional()
      .describe('Name - User-supplied label, e.g. work laptop or CI'),

    tokenHash: z
      .string()
      .nullable()
      .optional()
      .hidden()
      .describe(
        'Token Hash - SHA-256 of the secret; the plaintext is never stored',
      ),

    tokenPrefix: z
      .string()
      .nullable()
      .optional()
      .describe('Token Prefix - First characters of the secret, for display'),

    canPublish: z
      .boolean()
      .nullable()
      .optional()
      .describe(
        'Can Publish - Narrows the token; publish also requires the publisher role',
      ),

    expiresAt: z.coerce
      .date()
      .nullable()
      .optional()
      .describe('Expires At - Optional hard expiry'),

    lastUsedAt: z.coerce
      .date()
      .nullable()
      .optional()
      .describe(
        'Last Used At - Stamped on use, throttled to about once an hour',
      ),

    revokedAt: z.coerce
      .date()
      .nullable()
      .optional()
      .describe('Revoked At - Set when the token is revoked'),

    publishedFromRepository: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Published From Repository - The verified `repository` claim, recorded on every version this token publishes',
      ),

    publishedFromWorkflow: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Published From Workflow - The workflow filename read out of the verified `workflow_ref` claim',
      ),

    publishedFromRunId: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Published From Run Id - The verified `run_id` claim, which ties a published version back to one workflow run',
      ),

    actor: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Actor - The verified `actor` claim, the GitHub user who set the run off',
      ),

    userId: z
      .number()
      .nullable()
      .optional()
      .hidden()
      .describe('Foreign key to User'),

    npmPackageId: z
      .number()
      .nullable()
      .optional()
      .presentAs('reference')
      .describe('Foreign key to NpmPackage'),

    user: belongsTo('User'),

    loginSession: hasOne('LoginSession', 'tokenId'),

    npmPackage: references('NpmPackage'),
  })
  .describe(
    'A bearer credential the npm CLI presents on every registry request, stored only as a hash.',
  )
  .as('RegistryToken');

export { registryToken as registryTokenSchema };
